import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/**
 * The one place a wall-clock string meets a `Dayjs`.
 *
 * The API's date-times carry no zone: `2026-03-10T09:00:00` is nine o'clock at the clinic,
 * whoever is looking. `library/dateTimeHelper` exists to keep them out of `Date`, and this
 * file is the same rule for the pickers -- a `Dayjs` is created at the boundary, handed
 * straight to the control, and turned back into a string the moment it comes out. Form
 * state stays `string` everywhere, so every Yup schema still says `yup.string()` and every
 * request body carries exactly what it always did.
 *
 * That is also why this is the only module that **calls** dayjs on a picker's behalf. The
 * three wrappers in `components/pickers` import `Dayjs` as a type, to name what a picker
 * hands back before it is converted here, and nothing else in the app touches it --
 * `dashboardPeriod` uses dayjs for its own unrelated year arithmetic. Keep it that way: a
 * `dayjs(...)` call anywhere near a form value is how a wall clock starts drifting.
 *
 * Nothing here goes near `toISOString` or `Date`, both of which re-express a moment in UTC
 * and would quietly move an appointment for anybody west of Greenwich.
 */

/**
 * The formats the pickers render and parse.
 *
 * Fixed rather than taken from the locale, and deliberately so: `AdapterDayjs` asks dayjs
 * for `L`, and dayjs's `en` locale is *American* -- it writes 10 March 2026 as `03/10/2026`
 * and puts the clock on a 12-hour cycle. A Romanian clinic reading the English UI still
 * writes 10.03.2026 at 09:00.
 */
export const PICKER_DATE_FORMAT = 'DD.MM.YYYY';
export const PICKER_TIME_FORMAT = 'HH:mm';
export const PICKER_DATE_TIME_FORMAT = 'DD.MM.YYYY HH:mm';

/**
 * Midday, not midnight.
 *
 * A bare date has to be given *some* hour before dayjs will hold it, and midnight is the
 * worst available choice: it is the hour a clock change moves. Anchoring at noon leaves
 * twelve hours of slack either side of any transition, so the calendar day a picker was
 * handed is always the calendar day it gives back.
 */
const DAY_ANCHOR_TIME = 'T12:00:00';

/** Likewise a bare time needs a day. 1 January is nowhere near a transition. */
const TIME_ANCHOR_DATE = '2000-01-01T';

const orNull = (candidate: Dayjs): Dayjs | null => (candidate.isValid() ? candidate : null);

/** An ISO date ("2026-03-10") as the value a date picker holds. */
export const toPickerDate = (isoDate?: string | null): Dayjs | null =>
    isoDate ? orNull(dayjs(`${isoDate.slice(0, 10)}${DAY_ANCHOR_TIME}`)) : null;

/** The reverse. Formatted rather than serialised, so it never passes through UTC. */
export const fromPickerDate = (value: Dayjs | null): string =>
    value && value.isValid() ? value.format('YYYY-MM-DD') : '';

/** A wall-clock time ("08:00", or the API's "08:00:00") as a time picker's value. */
export const toPickerTime = (apiTime?: string | null): Dayjs | null =>
    apiTime ? orNull(dayjs(`${TIME_ANCHOR_DATE}${apiTime.slice(0, 5)}:00`)) : null;

/** The reverse, as `HH:mm` -- the shape `toApiTime` already knows how to send. */
export const fromPickerTime = (value: Dayjs | null): string =>
    value && value.isValid() ? value.format('HH:mm') : '';

/** A local wall clock ("2026-01-21T13:00", or the API's `:00`-suffixed form). */
export const toPickerDateTime = (wallClock?: string | null): Dayjs | null =>
    wallClock ? orNull(dayjs(wallClock.slice(0, 19))) : null;

/**
 * The reverse, **without seconds**.
 *
 * That omission is load-bearing rather than cosmetic: it is exactly what
 * `toDateTimeInputValue` always emitted, so `addMinutes` and every create-appointment
 * request downstream keep the shape they already handle and no call site has to change.
 */
export const fromPickerDateTime = (value: Dayjs | null): string =>
    value && value.isValid() ? value.format('YYYY-MM-DDTHH:mm') : '';
